import React, { useRef, useState, useEffect } from 'react';
import { Box } from '@mui/system';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { useTheme } from '@mui/material';

interface VideoPlayerForStockVideosProps {
  url: string;
  height?: string;
}

export const VideoPlayerForStockVideos: React.FC<VideoPlayerForStockVideosProps> = ({
  url,
  height,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Reset states when URL changes
      setIsMetadataLoaded(false);
      setDuration(0);
      setCurrentTime(0);
      setIsPlaying(false);

      const handleMetadata = () => {
        setDuration(video.duration);
        setIsMetadataLoaded(true);
      };

      const handleTimeUpdate = () => {
        setCurrentTime(video.currentTime);
      };

      const handleVideoEnded = () => {
        setIsPlaying(false);
        setCurrentTime(video.duration);
      };

      video.addEventListener('loadedmetadata', handleMetadata);
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('ended', handleVideoEnded);

      // error handling
      video.addEventListener('error', () => {
        console.error('Error loading video:', video.error);
        setIsMetadataLoaded(false);
      });

      return () => {
        video.removeEventListener('loadedmetadata', handleMetadata);
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('ended', handleVideoEnded);
      };
    }
  }, [url]); // Add url to dependency array to handle url changes

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch((error) => {
          console.error('Error playing video:', error);
          setIsPlaying(false);
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (time: number): string => {
    if (!isFinite(time) || isNaN(time)) {
      return '0:00';
    }
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen().catch((err) => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen().catch((err) => {
        console.error('Error attempting to exit fullscreen:', err);
      });
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, []);

  return (
    <Box
      ref={containerRef}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      style={{ maxWidth: '430px', width: '100%' }}
    >
      <video
        ref={videoRef}
        key={url}
        style={{
          background: '#000',
          borderRadius: '16px 16px 0 0',
          height: isFullScreen ? '100vh' : height,
          width: '100%',
          maxWidth: isFullScreen ? '100%' : '430px',
        }}
      >
        <source src={url} type="video/mp4" />
        <source src={url} type="video/quicktime" />
      </video>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        maxWidth={isFullScreen ? '100%' : '430px'}
        padding="16px"
        style={{
          background: theme.palette.background.primary,
          borderRadius: isFullScreen ? '0' : '0 0 16px 16px',
        }}
      >
        <button
          onClick={togglePlay}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '24px',
          }}
        >
          {isPlaying ? (
            <PauseCircleOutlineIcon color="primary" fontSize="large" />
          ) : (
            <PlayCircleOutlineIcon color="primary" fontSize="large" />
          )}
        </button>
        <div>
          {formatTime(currentTime)} / {isMetadataLoaded ? formatTime(duration) : '0:00'}
        </div>
        <button
          onClick={toggleFullScreen}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '24px',
          }}
        >
          {isFullScreen ? '⤓' : '⤢'}
        </button>
      </Box>
    </Box>
  );
};
